import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AsmConfig } from '@spartacus/asm/root';
import { translationChunksConfig, translations } from '@spartacus/assets';
import {
  AuthGuard,
  AuthRedirectService,
  CategoryPageMetaResolver,
  ContextServiceMap,
  FeaturesConfig,
  HttpErrorHandler,
  I18nConfig,
  OccConfig,
  ProductSearchAdapter,
  RoutingConfig,
  SiteContextConfigInitializer,
  provideConfig,
} from '@spartacus/core';
import { PWAModuleConfig, ProductListComponentService, ViewConfig } from '@spartacus/storefront';
import { RECAPTCHA_LANGUAGE, RecaptchaFormsModule, RecaptchaModule } from 'ng-recaptcha';
import { CookieModule } from 'ngx-cookie';
import { NgxImageZoomModule } from 'ngx-image-zoom';
import { environment } from 'src/environments/environment';
import { AppComponent } from './app.component';
import { ShimanoProductSearchAdapter } from './core/occ/adapters/product/shimano-product-search.adapter';
import { AnonymousInterceptor } from './helpers/anonymous.interceptor';
import { ShimanoAuthErrorInterceptor } from './helpers/shimano-auth-error.interceptor';
import { SiteContextInterceptor } from './helpers/site-context.interceptor';
import { ShimanoI18nModule } from './i18n/i18n.module';
import { LayoutConfigModule } from './layout/layout.module';
import { MultiSiteModule, shimanoServiceMapFactory } from './multi-site/multi-site.module';
import { AppRoutingModule } from './routing/routing.module';
import { ShimanoAuthRedirectService } from './service/auth/shimano-auth-redirect.service';
import { BadRequestHandler } from './service/error/bad-request.handler';
import { ShimanoForbiddenRequestHandlerService } from './service/error/shimano-forbidden-request-handler.service';
import { ShimanoOccConfigLoaderService } from './service/occ-config-loader/shimano-occ-config-loader.service';
import { ShimanoCategoryPageMetaResolver } from './service/product/shimano-category-page-meta.resolver';
import { ShimanoProductListComponentService } from './service/product/shimano-product-list-component.service';
import { CustomerServiceContentModule } from './spartacus/features/customer-service/customer-service-content/customer-service-content.module';
import { SrpToggleComponent } from './spartacus/features/header/srp-toggle/srp-toggle.component';
import { ShimanoImageZoomModule } from './spartacus/features/shimano-image-zoom/shimano-image-zoom.module';
import { TawkLiveChatComponent } from './spartacus/features/tawk-live-chat/tawk-live-chat.component';
import { CustomAuthGuard } from './spartacus/features/user/login/guards/custom-auth.guard';
import { SpartacusModule } from './spartacus/spartacus.module';
import { currentLanguage } from './utility/current-language';
import { CurrentLanguageSelected } from './utility/current-language-selected';
@NgModule({
  declarations: [AppComponent, SrpToggleComponent, TawkLiveChatComponent],
  imports: [
    // this is for SSR ENABLED
    // environment.production ? BrowserModule.withServerTransition({ appId: 'spartacus-app' }) : BrowserModule,
    BrowserModule,
    AppRoutingModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    SpartacusModule,
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true,
    }),
    CookieModule.forRoot(),
    LayoutConfigModule,
    MultiSiteModule,
    BrowserAnimationsModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    ShimanoI18nModule,
    RecaptchaFormsModule,
    RecaptchaModule,
    ReactiveFormsModule,
    NgxImageZoomModule,
    CustomerServiceContentModule,
    ShimanoImageZoomModule,
  ],
  providers: [
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    { provide: APP_ID, useValue: 'spartacus-app' },
    provideClientHydration(),
    provideConfig(<RoutingConfig>{
      // custom routing configuration for e2e testing
      routing: {
        protected: true,
        routes: {
          globalRedirect: {
            paths: ['global'],
            protected: false,
          },
        },
      },
    }),
    provideConfig(<OccConfig>{
      backend: {
        occ: {
          prefix: environment.prefix,
          legacy: false,
        },
      },
      authentication: {
        client_id: environment.clientID,
        client_secret: environment.clientSecret,
      },
    }),
    provideConfig(<I18nConfig>{
      // we bring in static translations to be up and running soon right away
      i18n: {
        resources: translations,
        chunks: translationChunksConfig,
        fallbackLang: 'en',
      },
    }),
    provideConfig(<FeaturesConfig>{
      features: {
        level: '2211.32',
      },
    }),
    provideConfig(<ViewConfig>{
      view: {
        defaultPageSize: 24,
        infiniteScroll: {
          active: true,
          productLimit: 500,
          showMoreButton: false,
        },
      },
    }),
    provideConfig(<PWAModuleConfig>{
      pwa: {
        enabled: false,
      },
    }),
    provideConfig(<AsmConfig>{
      asm: {
        agentSessionTimer: {
          startingDelayInSeconds: 14400,
        },
      },
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: SiteContextInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: ShimanoAuthErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: AnonymousInterceptor,
      multi: true,
    },
    {
      provide: HttpErrorHandler,
      useExisting: BadRequestHandler,
      multi: true,
    },
    {
      provide: HttpErrorHandler,
      useExisting: ShimanoForbiddenRequestHandlerService,
      multi: true,
    },
    {
      provide: RECAPTCHA_LANGUAGE,
      deps: [CurrentLanguageSelected],
      useFactory: currentLanguage,
    },
    { provide: 'googleTagManagerId', useValue: environment.gtmId },
    { provide: 'googleTagManagerAuth', useValue: environment.gtmAuth },
    { provide: 'googleTagManagerPreview', useValue: environment.gtmPreview },
    { provide: 'googleTagManagerCookie', useValue: environment.gtmCookie },
    { provide: AuthGuard, useClass: CustomAuthGuard },
    {
      provide: ProductListComponentService,
      useClass: ShimanoProductListComponentService,
    },
    {
      provide: CategoryPageMetaResolver,
      useClass: ShimanoCategoryPageMetaResolver,
    },
    {
      provide: ContextServiceMap,
      useFactory: shimanoServiceMapFactory,
    },
    {
      provide: SiteContextConfigInitializer,
      useClass: ShimanoOccConfigLoaderService,
    },
    {
      provide: AuthRedirectService,
      useClass: ShimanoAuthRedirectService,
    },
    {
      provide: ProductSearchAdapter,
      useClass: ShimanoProductSearchAdapter,
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
